import React, { createContext, useContext } from 'react';
import { Link, Outlet, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Eye,
  Flag,
  Globe,
  Building2,
  Search,
  Rocket,
  Users,
  Calendar,
  AlertTriangle,
  DollarSign,
  Truck,
  FileText,
  PieChart,
  ShieldAlert,
  PenLine,
  Activity,
  CreditCard,
  FileBarChart,
  GitBranch,
  UserPlus,
  Briefcase,
  Package,
  ClipboardCheck,
  Sparkles,
  Settings,
  Rocket as RocketIcon,
  ScrollText,
  ShieldCheck,
} from 'lucide-react';
import { fetchStudy } from '@/api/endpoints';
import type { Study } from '@/types';

// ─── Study context ──────────────────────────────────────────────────────────

interface StudyContextValue {
  study: Study | undefined;
  studyId: string;
  isLoading: boolean;
}

const StudyContext = createContext<StudyContextValue>({
  study: undefined,
  studyId: '',
  isLoading: true,
});

export function useStudyContext() {
  return useContext(StudyContext);
}

// ─── Tab navigation definition ──────────────────────────────────────────────

interface TabItem {
  label: string;
  to: string;
  icon: React.ElementType;
}

const studyTabs: TabItem[] = [
  { label: 'Overview', to: '/overview', icon: Eye },
  { label: 'Setup', to: '/setup', icon: Settings },
  { label: 'Startup', to: '/startup', icon: RocketIcon },
  { label: 'Milestones', to: '/milestones', icon: Flag },
  { label: 'Protocol', to: '/protocol-versions', icon: ScrollText },
  { label: 'Regulatory', to: '/regulatory', icon: ShieldCheck },
  { label: 'Countries', to: '/countries', icon: Globe },
  { label: 'Sites', to: '/sites', icon: Building2 },
  { label: 'Feasibility', to: '/feasibility', icon: Search },
  { label: 'Activation', to: '/activation', icon: Rocket },
  { label: 'Enrollment', to: '/enrollment', icon: Users },
  { label: 'Monitoring', to: '/monitoring', icon: Calendar },
  { label: 'Issues', to: '/issues', icon: AlertTriangle },
  { label: 'Financials', to: '/financials', icon: DollarSign },
  { label: 'Vendors', to: '/vendors', icon: Truck },
  { label: 'Documents', to: '/documents', icon: FileText },
  { label: 'RBQM', to: '/rbqm', icon: Activity },
  { label: 'Safety', to: '/safety', icon: ShieldAlert },
  { label: 'Signatures', to: '/signatures', icon: PenLine },
  { label: 'Subjects', to: '/subjects', icon: UserPlus },
  { label: 'Supply', to: '/supply', icon: Package },
  { label: 'Resources', to: '/resources', icon: Briefcase },
  { label: 'Closeout', to: '/closeout', icon: ClipboardCheck },
  { label: 'AI Insights', to: '/insights', icon: Sparkles },
  { label: 'Reports', to: '/reporting', icon: PieChart },
  { label: 'Report Builder', to: '/report-builder', icon: FileBarChart },
  { label: 'Payments', to: '/payments-export', icon: CreditCard },
  { label: 'Workflows', to: '/workflow-config', icon: GitBranch },
];

// ─── Status badge ───────────────────────────────────────────────────────────

const statusColors: Record<string, string> = {
  Draft: 'bg-gray-100 text-gray-700',
  'In Startup': 'bg-blue-100 text-blue-700',
  Enrolling: 'bg-green-100 text-green-700',
  'Fully Enrolled': 'bg-emerald-100 text-emerald-700',
  'In Treatment': 'bg-purple-100 text-purple-700',
  Closed: 'bg-red-100 text-red-700',
  Archived: 'bg-gray-200 text-gray-600',
};

function StatusBadge({ status }: { status: string }) {
  const color = statusColors[status] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}>
      {status}
    </span>
  );
}

// ─── Phase badge ────────────────────────────────────────────────────────────

function PhaseBadge({ phase }: { phase: string }) {
  return (
    <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
      {phase}
    </span>
  );
}

// ─── Study header ───────────────────────────────────────────────────────────

function StudyHeader({ study }: { study: Study | undefined; isLoading: boolean }) {
  if (!study) {
    return (
      <div className="animate-pulse">
        <div className="h-6 w-72 rounded bg-gray-200" />
        <div className="mt-1 h-4 w-48 rounded bg-gray-200" />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">{study.protocolNumber}</h1>
          <PhaseBadge phase={study.phase} />
          <StatusBadge status={study.status} />
        </div>
        <p className="mt-1 text-sm text-gray-500">{study.title}</p>
      </div>
      <div className="flex items-center gap-6 text-sm text-gray-500">
        <div>
          <span className="font-medium text-gray-700">{study.activeSites}</span>
          <span className="ml-1">/ {study.totalSites} sites</span>
        </div>
        <div>
          <span className="font-medium text-gray-700">{study.actualEnrollment}</span>
          <span className="ml-1">/ {study.targetEnrollment} enrolled</span>
        </div>
        <div>
          <span className="font-medium text-gray-700">{study.totalCountries}</span>
          <span className="ml-1">countries</span>
        </div>
      </div>
    </div>
  );
}

// ─── Tab navigation ─────────────────────────────────────────────────────────

function StudyTabs({ studyId }: { studyId: string }) {
  return (
    <div className="overflow-x-auto border-b border-gray-200">
      <nav className="-mb-px flex gap-0" aria-label="Study tabs">
        {studyTabs.map((tab) => (
          <Link
            key={tab.to}
            to={`/studies/$studyId${tab.to}` as any}
            params={{ studyId } as any}
            className="flex shrink-0 items-center gap-1.5 border-b-2 border-transparent px-4 py-2.5 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 [&.active]:border-indigo-600 [&.active]:text-indigo-600"
            activeProps={{ className: 'active border-indigo-600 text-indigo-600' }}
            activeOptions={{ exact: true }}
          >
            <tab.icon size={15} />
            {tab.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}

// ─── Main layout ────────────────────────────────────────────────────────────

export default function StudyLayout() {
  const { studyId } = useParams({ from: '/studies/$studyId' });

  const { data: study, isLoading } = useQuery({
    queryKey: ['study', studyId],
    queryFn: () => fetchStudy(studyId),
    enabled: !!studyId,
  });

  return (
    <StudyContext.Provider value={{ study, studyId, isLoading }}>
      <div className="space-y-4">
        {/* Study header */}
        <div className="rounded-lg border border-gray-200 bg-white px-6 py-4">
          <StudyHeader study={study} isLoading={isLoading} />
        </div>

        {/* Tab navigation */}
        <div className="rounded-lg border border-gray-200 bg-white">
          <StudyTabs studyId={studyId} />
          <div className="p-6">
            <Outlet />
          </div>
        </div>
      </div>
    </StudyContext.Provider>
  );
}
